// apps/web/src/shared/phoneError.ts
//
// Shared helper for any RHF form whose phone field validates against the
// `phoneSchema` from `@magimp/shared`.
//
// `phoneSchema` emits the literal `'invalid E.164 phone'` message for any
// non-strict E.164 input. RHF surfaces it verbatim through
// `errors.phone.message`, which would otherwise leak as bare English copy
// into the form. We translate that single known message via i18n; any other
// message is passed through unchanged so future, less-stable validators do
// not silently swallow an unmapped error.
//
// Lives under `apps/web/src/shared/` (alongside `pickChangedFields`) rather
// than inside any one feature, because consumer-side forms in
// `features/{users,profile,...}` all need it.

const RAW_INVALID_PHONE = 'invalid E.164 phone';

/**
 * @aiPurpose translate the raw `phoneSchema` error message to a localized
 *   form-level alert. The caller passes the already-translated copy for
 *   the chosen i18n key (e.g. `users:errors.invalidPhone` or
 *   `profile:errors.invalidPhone`) so the helper stays free of i18n type
 *   plumbing across forms with differing namespace tuples.
 * @aiInvariant only the literal `'invalid E.164 phone'` is replaced; any
 *   other message (including `undefined`) is returned verbatim.
 */
export function phoneError(
  message: string | undefined,
  invalidPhoneCopy: string,
): string | undefined {
  if (!message) return undefined;
  if (message === RAW_INVALID_PHONE) return invalidPhoneCopy;
  return message;
}
